import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import ViewVideoModal from './ViewVideoModal'
import { bindActionCreators } from 'redux'
import AddEditVideoModal from './AddEditVideoModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon, InformationIcon } from '../../../icons'
import { videosUploadDeletePermission, videosUploadWritePermission } from '../../../utils/permissionValidation'
import { capitalizeWords, groupModelsByDevice } from '../../../utils/functions'
import { formatDateMonthDayYear } from '../../../utils/filters/date'
import { clearVideosMessages, loadAdminVideoCategories, loadAdminVideoTypes, loadVideosUpload, removeVideo } from '../../../actions/videos'
import { loadAccessLevels, loadLanguages, loadRegions, loadModels } from '../../../actions/shared'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { formatSnakeCase, buildSnakeLabels } from '../../../utils/filters/filters'
import { videoUploadInstructions } from '../../../utils/fixedUrls'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'videoId',
    order: 'asc',
    translationKey: 'video_id_asc',
  },
  {
    field: 'videoId',
    order: 'desc',
    translationKey: 'video_id_desc',
  },
  {
    field: 'title',
    order: 'asc',
    translationKey: 'title_asc',
  },
  {
    field: 'title',
    order: 'desc',
    translationKey: 'title_desc',
  },
  {
    field: 'videoLastUpdateDate',
    order: 'asc',
    translationKey: 'last_updated_asc',
  },
  {
    field: 'videoLastUpdateDate',
    order: 'desc',
    translationKey: 'last_updated_desc',
  },
  {
    field: 'accessLevel',
    order: 'asc',
    translationKey: 'access_level_asc',
  },
  {
    field: 'accessLevel',
    order: 'desc',
    translationKey: 'access_level_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Videos = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [languages, setLanguages] = useState([])
  const [videosTableData, setVideosTableData] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null)
  const [selectedFilterAccessLevel, setSelectedFilterAccessLevel] = useState(null)
  const [statusesFilter, setStatusesFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [selectedFilterLanguage, setSelectedFilterLanguage] = useState(null)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedVideo, setExpandedVideo] = useState(0)
  const [showViewVideoModal, setShowViewVideoModal] = useState(false)
  const [showAddEditVideoModal, setShowAddEditVideoModal] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [showDeleteVideoConfirmModal, setShowDeleteVideoConfirmModal] = useState(false)
  const [videoIdToDelete, setVideoIdToDelete] = useState(null)
  const videoStatuses = ['active', 'inactive']
  const categoryLabels = buildSnakeLabels(props.videoCategories, (t) => t.name)

  useEffect(() => {
    if (props.videoCategories.length === 0) {
      props.actions.loadAdminVideoCategories()
    }

    if (props.videoTypes.length === 0) {
      props.actions.loadAdminVideoTypes()
    }

    if (props.videoRegions.length === 0) {
      props.actions.loadRegions()
    }

    if (props.accessLevels.length === 0) {
      props.actions.loadAccessLevels()
    }

    if (props.languages.length === 0) {
      props.actions.loadLanguages()
    }

    if (props.videoModels.length === 0) {
      props.actions.loadModels()
    }
  }, [props.actions])

  useEffect(() => {
    const sortedLanguageCodes = props.languages
      .map((lang) => ({
        code: `${lang}-lang`,
        name: t([`filter_context.${lang}-lang`, lang]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => item.code)

    setLanguages(sortedLanguageCodes)
  }, [props.languages])

  const getUniqueVideoRegions = (regions) => {
    if (regions.length === 0) {
      return ''
    }

    return regions
      .map((region) => region.name)
      .sort((a, b) => a.localeCompare(b))
      .join(', ')
  }

  useEffect(() => {
    const tableData = props.videos.map((video, index) => {
      return {
        index,
        identifier: video.id,
        videoId: video.videoId,
        title: video.title,
        lastUpdated: video.videoLastUpdateDate ? formatDateMonthDayYear(video.videoLastUpdateDate) : '',
        accessLevel: video.accessLevel,
        model: groupModelsByDevice(video.models),
        category: video.categories.join(', '),
        region: getUniqueVideoRegions(video.regions || []),
        status: video.isActive ? videoStatuses[0] : videoStatuses[1],
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditVideo(video.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditVideo(video.id, true)
            },
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(video.id)
            },
            requiredPermission: videosUploadDeletePermission,
            isDisabled: video.isActive,
          },
        ],
      }
    })

    setVideosTableData(tableData)
  }, [props.videos])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearVideosMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearVideosMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadVideos(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadVideos(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterAccessLevel, selectedFilterLanguage, selectedFilterModel, selectedFilterCategory, statusesFilter, orderBy])

  const handleLoadVideos = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadVideosUpload(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterAccessLevel(null)
    setSelectedFilterCategory(null)
    setSelectedFilterLanguage(null)
    setSelectedFilterModel(null)
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {
      startPage: 1,
    }

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterCategory) {
      queryParams = { ...queryParams, category: selectedFilterCategory.name }
    }

    if (selectedFilterAccessLevel) {
      queryParams = { ...queryParams, accessLevel: selectedFilterAccessLevel }
    }

    if (selectedFilterLanguage) {
      queryParams = { ...queryParams, languageCode: selectedFilterLanguage }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === videoStatuses[0] ? true : false }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const renderCompanyStatus = (status) => {
    switch (status) {
      case videoStatuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case videoStatuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const handleOpenViewEditVideo = (videoId, isEditModal) => {
    const video = props.videos.find((video) => video.id === videoId)

    if (video) {
      isEditModal ? setShowAddEditVideoModal(true) : setShowViewVideoModal(true)
      setSelectedVideo(video)
    }
  }

  const showConfirmationModal = (id) => {
    setVideoIdToDelete(id)
    setShowDeleteVideoConfirmModal(true)
  }

  const handleDeleteVideo = () => {
    setShowDeleteVideoConfirmModal(false)
    props.actions.removeVideo(videoIdToDelete)
  }

  const closeModal = () => {
    setShowAddEditVideoModal(false)
    setShowViewVideoModal(false)
    setSelectedVideo(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterModel || selectedFilterCategory || selectedFilterAccessLevel || selectedFilterLanguage || statusesFilter.length > 0}
            counterBadge={
              (selectedFilterModel && 1) +
              (selectedFilterCategory && 1) +
              (selectedFilterAccessLevel && 1) +
              (selectedFilterLanguage && 1) +
              statusesFilter.length
            }
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditVideoModal(true)} requiredPermission={videosUploadWritePermission}>
            {t('video_context.add_video')}
          </SimpleButton>
        </div>
        <div className="information-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="information-wrapper">
            <div className="title">
              {`${t('video_context.upload_instructions')} `}
              <span>
                <a href={videoUploadInstructions} target="_blank" rel="noreferrer" className="external-link">
                  {t('document_context.click_here')}
                </a>
              </span>
            </div>
          </div>
        </div>
        {isMobile && videosTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && videosTableData.length > 0 && (
          <GenericTable
            data={videosTableData}
            headers={[
              {
                title: t('video_id'),
                orderKey: 'videoId',
              },
              {
                title: t('title'),
                orderKey: 'title',
              },
              {
                title: t('access_level'),
                orderKey: 'accessLevel',
              },
              {
                title: t('model'),
              },
              {
                title: t('category'),
              },
              {
                title: t('region'),
              },
              {
                title: capitalizeWords(t('last_updated')),
                orderKey: 'videoLastUpdateDate',
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['videoId', 'title', 'accessLevel', 'model', 'category', 'region', 'lastUpdated', 'status', 'action']}
            keyRenderer={{
              accessLevel: (item) => t(item.accessLevel),
              status: (item) => {
                return <div className="status-wrapper">{renderCompanyStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(video) => handleOpenViewEditVideo(video.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          videosTableData.map((video, index) => (
            <div key={index} className="card" onClick={() => setExpandedVideo(index)}>
              <div className={'card-item' + (expandedVideo !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('title')}</div>
                <div className={'card-item-body' + (expandedVideo !== index ? ' align-center' : '')}>
                  <div>{video.title}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={video.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedVideo === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('video_id')}</div>
                    <div className="card-item-body">{video.videoId}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('model')}</div>
                    <div className="card-item-body">{video.model}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('category')}</div>
                    <div className="card-item-body">{video.category}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('region')}</div>
                    <div className="card-item-body">{video.region}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('access_level')}</div>
                    <div className="card-item-body">{t(video.accessLevel)}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{capitalizeWords(t('last_updated'))}</div>
                    <div className="card-item-body">{video.lastUpdated}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderCompanyStatus(video.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && videosTableData.length === 0 && (
          <EmptyPageResults title={t('video_context.no_videos_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: props.videoModels,
              input: selectedFilterModel,
              output: (filterModel) => {
                setSelectedFilterModel(filterModel)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedFilterCategory ? formatSnakeCase(selectedFilterCategory.name) : null,
              output: (newCategory) => {
                const matched = props.videoCategories.find((c) => formatSnakeCase(c.name) === newCategory)
                setSelectedFilterCategory(matched || null)
              },
            },
            {
              name: t('access_level'),
              type: FilterType.singleSelect,
              dataset: props.accessLevels.map((level) => level.name),
              input: selectedFilterAccessLevel,
              output: (filterAccessLevel) => {
                setSelectedFilterAccessLevel(filterAccessLevel)
              },
            },
            {
              name: t('language'),
              type: FilterType.singleSelect,
              dataset: languages,
              input: selectedFilterLanguage ? `${selectedFilterLanguage}-lang` : null,
              output: (filterLanguage) => {
                const language = filterLanguage?.split('-')[0] || null
                setSelectedFilterLanguage(language)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: videoStatuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditVideoModal && <AddEditVideoModal selectedVideo={selectedVideo} closeModal={closeModal} showSpinner={showSpinner} />}
      {showViewVideoModal && (
        <ViewVideoModal
          selectedVideo={selectedVideo}
          handleEditVideo={() => {
            setShowViewVideoModal(false)
            setShowAddEditVideoModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showDeleteVideoConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteVideoConfirmModal(false)}
          onAccept={() => handleDeleteVideo()}
          onCancel={() => setShowDeleteVideoConfirmModal(false)}
          message={t('video_context.confirm_delete_video')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={videosUploadDeletePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ videos, shared }) {
  return {
    videos: videos?.videosUpload || [],
    videoCategories: videos?.adminVideoCategories || [],
    videoTypes: videos?.adminVideoTypes || [],
    videoRegions: shared?.regions || [],
    videoModels: [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])],
    accessLevels: shared?.accessLevels || [],
    languages: shared?.languages?.map((l) => l.languageCode) || [],
    isLoading: videos?.isLoading,
    canLoadMore: videos?.canLoadMoreVideosUpload,
    errorMessage: videos?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadVideosUpload,
        loadAdminVideoCategories,
        loadAdminVideoTypes,
        loadAccessLevels,
        loadLanguages,
        loadRegions,
        loadModels,
        removeVideo,
        clearVideosMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Videos)

